import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { RootState } from '@/store/store'
import { Gig, UpdateGigData } from '@/types/gig'
import {
    FiSave,
    FiArrowLeft,
    FiTag,
    FiMapPin,
    FiClock,
    FiPlus,
    FiX,
    FiAlertCircle,
    FiTrash2,
    FiImage
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import GigImageUpload from '@/components/ui/GigImageUpload'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { gigApi } from '@/services/api/gigs'
import { gigCategoryApi, GigCategory } from '@/services/api/gigCategories'

const EditGigPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const { showError, showSuccess } = useSnackbarContext()

    const [gig, setGig] = useState<Gig | null>(null)
    const [categories, setCategories] = useState<GigCategory[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        subcategory: '',
        location: {
            address: '',
            location: '',
            serviceType: 'onsite' as 'remote' | 'onsite' | 'hybrid'
        },
        payment: {
            amount: '',
            currency: 'GHS',
            type: 'fixed' as 'fixed' | 'hourly' | 'commission'
        },
        duration: {
            estimated: '',
            flexible: true
        },
        requirements: [] as string[],
        status: 'active' as 'active' | 'filled' | 'completed' | 'cancelled',
        urgency: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
        tags: [] as string[],
        images: [] as string[],
        requireSubmission: true
    })

    const [newRequirement, setNewRequirement] = useState('')
    const [newTag, setNewTag] = useState('')

    useEffect(() => {
        if (!isAuthenticated || user?.role !== 'employer') {
            navigate('/dashboard')
            return
        }

        const fetchData = async () => {
            if (!id) {
                navigate('/dashboard')
                return
            }

            try {
                // Fetch both gig data and categories
                const [gigResponse, categoriesResponse] = await Promise.all([
                    gigApi.getGigById(id),
                    gigCategoryApi.getCategories()
                ]);

                // @ts-expect-error - Intentional nested data structure for API response
                const gigData = gigResponse.data.data.gig;
                // @ts-expect-error - Intentional nested data structure for API response
                const categoriesData = categoriesResponse.data.data.categories;

                // Check if user owns this gig
                const employerId = typeof gigData.employer === 'string' ? gigData.employer : gigData.employer._id;
                if (employerId !== user._id) {
                    showError('You can only edit your own gigs');
                    navigate('/dashboard');
                    return;
                }

                setCategories(categoriesData);
                setGig(gigData);
                setFormData({
                    title: gigData.title,
                    description: gigData.description,
                    category: typeof gigData.category === 'string' ? gigData.category : gigData.category._id,
                    subcategory: gigData.subcategory || '',
                    location: {
                        address: gigData.location.address || '',
                        location: gigData.location.location,
                        serviceType: gigData.location.serviceType
                    },
                    payment: {
                        amount: gigData.payment.amount.toString(),
                        currency: gigData.payment.currency,
                        type: gigData.payment.type as 'fixed' | 'hourly' | 'commission'
                    },
                    duration: {
                        estimated: gigData.duration.estimated,
                        flexible: gigData.duration.isFlexible || gigData.duration.flexible
                    },
                    requirements: gigData.requirements || [],
                    status: gigData.status,
                    urgency: gigData.urgency || 'medium',
                    tags: gigData.tags || [],
                    images: gigData.images || [],
                    requireSubmission: gigData.requireSubmission !== undefined ? gigData.requireSubmission : true
                });

            } catch (error) {
                console.error('Failed to fetch data:', error)
                showError('Failed to load gig details')
                navigate('/dashboard')
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [id, user, isAuthenticated, navigate])

    // Handle escape key for delete dialog
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showDeleteDialog) {
                handleCancelDelete()
            }
        }

        if (showDeleteDialog) {
            document.addEventListener('keydown', handleEscapeKey)
        }

        return () => document.removeEventListener('keydown', handleEscapeKey)
    }, [showDeleteDialog])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target

        if (name.includes('.')) {
            const [parent, child] = name.split('.')
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev as any)[parent],
                    [child]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
                }
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
            }))
        }
    }

    const addRequirement = () => {
        if (newRequirement.trim() && !formData.requirements.includes(newRequirement.trim())) {
            setFormData(prev => ({
                ...prev,
                requirements: [...prev.requirements, newRequirement.trim()]
            }))
            setNewRequirement('')
        }
    }

    const removeRequirement = (requirementToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.filter(req => req !== requirementToRemove)
        }))
    }

    const addTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim().toLowerCase()]
            }))
            setNewTag('')
        }
    }

    const removeTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }))
    }

    // Image handling functions
    const handleImagesChange = (images: string[]) => {
        setFormData(prev => ({
            ...prev,
            images
        }))
    }

    const handleImageUploadError = (error: string) => {
        showError(`Image upload failed: ${error}`)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validation
        if (!formData.title.trim() || !formData.description.trim()) {
            showError('Title and description are required')
            return
        }

        if (!formData.category) {
            showError('Please select a category')
            return
        }

        if (!formData.payment.amount || parseFloat(formData.payment.amount) <= 0) {
            showError('Please enter a valid payment amount')
            return
        }

        if (!formData.location.location.trim()) {
            showError('Please enter a location')
            return
        }

        setSaving(true)

        try {
            const updateData: UpdateGigData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                category: formData.category,
                subcategory: formData.subcategory || undefined,
                location: {
                    location: formData.location.location.trim(),
                    serviceType: formData.location.serviceType
                },
                payment: {
                    amount: parseFloat(formData.payment.amount),
                    currency: formData.payment.currency,
                    type: formData.payment.type
                },
                duration: {
                    estimated: formData.duration.estimated.trim(),
                    flexible: formData.duration.flexible
                },
                requirements: formData.requirements.filter(req => req.trim()),
                status: formData.status,
                urgency: formData.urgency,
                tags: formData.tags,
                images: formData.images || [],
                requireSubmission: formData.requireSubmission
            };

            // Use real API call
            await gigApi.updateGig(id!, updateData);

            showSuccess('Gig updated successfully!');
            navigate(`/gigs/${id}`);

        } catch (error) {
            console.error('Failed to update gig:', error)
            showError('Failed to update gig. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    // Delete dialog handlers
    const handleDeleteClick = () => {
        setShowDeleteDialog(true)
    }

    const handleConfirmDelete = async () => {
        setShowDeleteDialog(false)
        setDeleting(true)

        try {
            // Use real API call
            await gigApi.deleteGig(id!);

            showSuccess('Gig deleted successfully');
            navigate('/dashboard/gigs/my-gigs');

        } catch (error) {
            console.error('Failed to delete gig:', error)
            showError('Failed to delete gig. Please try again.')
        } finally {
            setDeleting(false)
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteDialog(false)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (!gig) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center">
                    <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Gig Not Found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                        The gig you're trying to edit doesn't exist or you don't have permission to edit it.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back to Dashboard
                    </button>
                </div>
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>Edit Gig - {gig.title} - SideGO</title>
                <meta name="description" content="Edit your gig posting information and requirements" />
            </Helmet>

            <div className="max-w-4xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
                {/* Back Button */}
                <div className="mb-4 sm:mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <FiArrowLeft className="mr-2 flex-shrink-0" />
                        Back to gig
                    </button>
                </div>

                {/* Page Header */}
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                        Edit Gig
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        Update your gig information to attract the right talent
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 md:space-y-8">
                    {/* Basic Information */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Basic Information
                        </h2>

                        <div className="space-y-4 sm:space-y-6">
                            {/* Title */}
                            <div>
                                <label htmlFor="title" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Gig Title *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <FiTag className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="e.g. Need a logo design for my restaurant"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label htmlFor="description" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Description *
                                </label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                    rows={6}
                                    className="block w-full px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Describe what you need in detail..."
                                />
                            </div>

                            {/* Category and Status */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                                <div>
                                    <SelectDropdown
                                        label="Category *"
                                        value={formData.category}
                                        onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                                        options={categories.map(cat => ({
                                            value: cat._id,
                                            label: cat.name
                                        }))}
                                        placeholder="Select category"
                                    />
                                </div>

                                <div>
                                    <SelectDropdown
                                        label="Status"
                                        value={formData.status}
                                        onChange={(value) => setFormData(prev => ({ ...prev, status: value as 'active' | 'filled' | 'completed' | 'cancelled' }))}
                                        options={[
                                            { value: 'active', label: 'Active' },
                                            { value: 'filled', label: 'Filled' },
                                            { value: 'completed', label: 'Completed' },
                                            { value: 'cancelled', label: 'Cancelled' }
                                        ]}
                                        placeholder="Select status"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment and Duration */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Payment & Duration
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            {/* Payment Amount */}
                            <div>
                                <label htmlFor="payment.amount" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Budget (USD) *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-400 font-semibold text-xs sm:text-base">$</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="payment.amount"
                                        name="payment.amount"
                                        value={formData.payment.amount}
                                        onChange={handleInputChange}
                                        required
                                        min="1"
                                        className="block w-full pl-7 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            {/* Payment Type */}
                            <div>
                                <SelectDropdown
                                    label="Payment Type"
                                    value={formData.payment.type}
                                    onChange={(value) => setFormData(prev => ({ ...prev, payment: { ...prev.payment, type: value as 'fixed' | 'hourly' | 'commission' } }))}
                                    options={[
                                        { value: 'fixed', label: 'Fixed Price' },
                                        { value: 'hourly', label: 'Hourly Rate' }
                                    ]}
                                    placeholder="Select payment type"
                                />
                            </div>

                            {/* Duration */}
                            <div>
                                <label htmlFor="duration.estimated" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Estimated Duration
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <FiClock className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="duration.estimated"
                                        name="duration.estimated"
                                        value={formData.duration.estimated}
                                        onChange={handleInputChange}
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="e.g. 2 weeks, 5 days, 1 month"
                                    />
                                </div>
                            </div>

                            {/* Urgency */}
                            <div>
                                <SelectDropdown
                                    label="Priority Level"
                                    value={formData.urgency}
                                    onChange={(value) => setFormData(prev => ({ ...prev, urgency: value as 'low' | 'medium' | 'high' | 'urgent' }))}
                                    options={[
                                        { value: 'low', label: 'Low Priority' },
                                        { value: 'medium', label: 'Medium Priority' },
                                        { value: 'high', label: 'High Priority' }
                                    ]}
                                    placeholder="Select priority level"
                                />
                            </div>
                        </div>

                        {/* Flexible Duration Checkbox */}
                        <div className="mt-3 sm:mt-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="duration.flexible"
                                    checked={formData.duration.flexible}
                                    onChange={handleInputChange}
                                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:focus:ring-primary-600"
                                />
                                <span className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                                    Duration is flexible
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Location
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <div>
                                <label htmlFor="location.location" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Location *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                                        <FiMapPin className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="location.location"
                                        name="location.location"
                                        value={formData.location.location}
                                        onChange={handleInputChange}
                                        placeholder="e.g., New York, NY or Remote"
                                        required
                                        className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="location.address" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Specific Address (Optional)
                                </label>
                                <input
                                    type="text"
                                    id="location.address"
                                    name="location.address"
                                    value={formData.location.address}
                                    onChange={handleInputChange}
                                    className="block w-full px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Enter specific location or 'Remote'"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Require Submission Toggle */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                    Require Work Submission
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {formData.requireSubmission
                                        ? "Workers must submit deliverables online before payment is released"
                                        : "On-site work - no online submission required, payment released after completion"
                                    }
                                </p>
                            </div>
                            <div className="ml-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, requireSubmission: !prev.requireSubmission }))}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                        formData.requireSubmission
                                            ? 'bg-primary-600'
                                            : 'bg-gray-200 dark:bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            formData.requireSubmission ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <strong>ON:</strong> Best for remote work, design, programming, writing<br />
                            <strong>OFF:</strong> Best for cleaning, repairs, in-person services
                        </div>
                    </div>

                    {/* Requirements */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Requirements
                        </h2>

                        <div className="space-y-2 sm:space-y-3">
                            {/* Add Requirement */}
                            <div className="flex gap-1 sm:gap-2">
                                <input
                                    type="text"
                                    value={newRequirement}
                                    onChange={(e) => setNewRequirement(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            addRequirement()
                                        }
                                    }}
                                    className="flex-1 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Add a requirement..."
                                />
                                <button
                                    type="button"
                                    onClick={addRequirement}
                                    className="px-2 sm:px-3 py-1 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex-shrink-0"
                                >
                                    <FiPlus className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Current Requirements */}
                            <div className="space-y-1 sm:space-y-2">
                                {formData.requirements.map((requirement, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                    >
                                        <span className="text-xs sm:text-sm text-gray-900 dark:text-white">{requirement}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeRequirement(requirement)}
                                            className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0 ml-2"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Tags (Optional)
                        </h2>

                        <div className="space-y-3 sm:space-y-4">
                            {/* Add Tag */}
                            <div className="flex gap-1 sm:gap-2">
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            addTag()
                                        }
                                    }}
                                    className="flex-1 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Add a tag (e.g., urgent, remote, beginner-friendly)..."
                                />
                                <button
                                    type="button"
                                    onClick={addTag}
                                    className="px-2 sm:px-3 py-1 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex-shrink-0"
                                >
                                    <FiPlus className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Current Tags */}
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                                {formData.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs sm:text-sm font-medium rounded-full"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => removeTag(tag)}
                                            className="ml-1 sm:ml-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                                        >
                                            <FiX className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            <div className="flex items-center">
                                <FiImage className="mr-2 flex-shrink-0 h-5 w-5 text-primary-600 dark:text-primary-400" />
                                Gig Images
                                <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                            </div>
                        </h2>

                        <div className="space-y-3 sm:space-y-4">
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Add up to 3 images to showcase your gig. High-quality images help attract more potential workers. Only JPEG, JPG, and PNG files are allowed.
                            </p>

                            <GigImageUpload
                                images={formData.images || []}
                                onImagesChange={handleImagesChange}
                                onUploadError={handleImageUploadError}
                                maxImages={3}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-row gap-2 sm:gap-3 md:gap-4 justify-end">
                        <button
                            type="button"
                            onClick={handleDeleteClick}
                            disabled={deleting}
                            className="inline-flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {deleting ? (
                                <>
                                    <LoadingSpinner size="sm" className="mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">Deleting...</span>
                                    <span className="sm:hidden">Deleting</span>
                                </>
                            ) : (
                                <>
                                    <FiTrash2 className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                                    <span className="hidden sm:inline">Delete Gig</span>
                                    <span className="sm:hidden">Delete</span>
                                </>
                            )}
                        </button>

                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saving ? (
                                <>
                                    <LoadingSpinner size="sm" className="mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">Saving Changes...</span>
                                    <span className="sm:hidden">Saving</span>
                                </>
                            ) : (
                                <>
                                    <FiSave className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                                    <span className="hidden sm:inline">Save Changes</span>
                                    <span className="sm:hidden">Save</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Custom Delete Dialog */}
                {showDeleteDialog && createPortal(
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                handleCancelDelete()
                            }
                        }}
                    >
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <FiAlertCircle className="w-6 h-6 text-red-500" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                        Delete Gig
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                        Are you sure you want to delete this gig? This action cannot be undone.
                                    </p>
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={handleCancelDelete}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleConfirmDelete}
                                            disabled={deleting}
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {deleting ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </>
    )
}

export default EditGigPage